// src/context/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

// Extrait TOUJOURS une string lisible depuis une erreur axios/Pydantic v2
export const extractError = (err) => {
  try {
    const detail = err?.response?.data?.detail;
    if (!detail) return err?.message || 'Une erreur est survenue';

    // String simple
    if (typeof detail === 'string') return detail;

    // Pydantic v2 : tableau [{type, loc, msg, input, ctx}, ...]
    if (Array.isArray(detail)) {
      return detail
        .map(d => {
          if (typeof d === 'string') return d;
          if (typeof d === 'object' && d !== null) {
            const loc = Array.isArray(d.loc) ? d.loc.join('.') : '';
            const msg = d.msg || d.message || JSON.stringify(d);
            return loc ? `${loc}: ${msg}` : msg;
          }
          return String(d);
        })
        .join(' — ');
    }

    if (typeof detail === 'object') {
      return detail.msg || detail.message || JSON.stringify(detail);
    }

    return String(detail);
  } catch {
    return 'Une erreur est survenue';
  }
};

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);

  // Récupère l'utilisateur enrichi (avec effective_permissions) depuis /admin/me
  const fetchEnrichedUser = useCallback(async (baseUser) => {
    try {
      const meRes = await api.get('/admin/me');
      return { ...baseUser, ...meRes.data };
    } catch {
      return baseUser;
    }
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem('wib_user');
    const token  = localStorage.getItem('wib_token');
    if (stored && token) {
      try {
        const u = JSON.parse(stored);
        setUser(u);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        // Rafraîchir les permissions en arrière-plan
        fetchEnrichedUser(u).then(enriched => {
          if (enriched !== u) {
            setUser(enriched);
            localStorage.setItem('wib_user', JSON.stringify(enriched));
          }
        });
      } catch {
        localStorage.removeItem('wib_user');
        localStorage.removeItem('wib_token');
      }
    }
    setLoading(false);
  }, [fetchEnrichedUser]);

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    const { access_token, user: u } = res.data;
    localStorage.setItem('wib_token', access_token);
    api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

    // Enrichir avec les permissions effectives
    const enriched = await fetchEnrichedUser(u);
    localStorage.setItem('wib_user', JSON.stringify(enriched));
    setUser(enriched);
    return enriched;
  };

  const logout = () => {
    localStorage.removeItem('wib_token');
    localStorage.removeItem('wib_user');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
  };

  // Recharger le profil/permissions (utile après une modification admin)
  const refreshUser = useCallback(async () => {
    if (!user) return;
    const enriched = await fetchEnrichedUser(user);
    setUser(enriched);
    localStorage.setItem('wib_user', JSON.stringify(enriched));
    return enriched;
  }, [user, fetchEnrichedUser]);

  // Vérifie si l'utilisateur a une permission (SUPER_ADMIN = all)
  const hasPermission = useCallback((key) => {
    if (!user) return false;
    if (user.is_superuser || user.role === 'SUPER_ADMIN') return true;
    return (user.effective_permissions || []).includes(key);
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshUser, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
